import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, Check, Building2, User, LayoutGrid, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WorkspaceItem {
  id: string;
  name: string;
  avatar?: string | null;
}

interface WorkspaceSwitcherProps {
  workspaces: WorkspaceItem[];
  equipos: WorkspaceItem[];
  activeId: string | null;
  activeType: 'personal' | 'equipo';
  onChange: (id: string, type: 'personal' | 'equipo') => void;
  onAbandon?: (id: string, type: 'personal' | 'equipo') => void;
}

function getInitials(name: string) {
  if (!name) return 'W';
  return name.substring(0, 2).toUpperCase();
}

export function WorkspaceSwitcher({
  workspaces,
  equipos,
  activeId,
  activeType,
  onChange,
  onAbandon,
}: Readonly<WorkspaceSwitcherProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position based on trigger button rect
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left: rect.left,
        width: 300,
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on scroll/resize so dropdown stays aligned
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);

  const activeItem = useMemo(() => {
    if (activeType === 'personal') return workspaces.find((w) => w.id === activeId);
    return equipos.find((e) => e.id === activeId);
  }, [activeId, activeType, workspaces, equipos]);

  const filteredWorkspaces = workspaces.filter((w) =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredEquipos = equipos.filter((e) =>
    e.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalItems = workspaces.length + equipos.length;

  let iconContent;
  if (activeItem?.avatar) {
    iconContent = <img src={activeItem.avatar} alt={activeItem.name} className="h-full w-full object-cover" />;
  } else if (activeItem) {
    iconContent = getInitials(activeItem.name);
  } else {
    iconContent = <LayoutGrid className="h-4 w-4" />;
  }

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl flex flex-col animate-in fade-in slide-in-from-top-2 duration-200"
    >
      {/* Search Bar (only if many items) */}
      {totalItems > 5 && (
        <div className="p-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar consultorio..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-slate-700 dark:text-slate-200 placeholder:text-slate-400"
              autoFocus
            />
          </div>
        </div>
      )}

      {/* List Area */}
      <div className="flex-1 overflow-y-auto max-h-87.5 p-2">
        {filteredWorkspaces.length === 0 && filteredEquipos.length === 0 && (
          <div className="p-4 text-center text-sm text-slate-500">
            No se encontraron resultados
          </div>
        )}

        {filteredWorkspaces.length > 0 && (
          <div className="mb-2 flex flex-col gap-1">
            <div className="px-4 pt-3 pb-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <User className="h-3 w-3 shrink-0" />
              Agendas Personales
            </div>
            {filteredWorkspaces.map((w) => (
              <div
                key={w.id}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors group hover:bg-slate-100 dark:hover:bg-slate-800/60',
                  activeId === w.id && activeType === 'personal'
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : ''
                )}
              >
                <button
                  onClick={() => { onChange(w.id, 'personal'); setIsOpen(false); }}
                  className="flex items-center gap-3 flex-1 text-left outline-none min-w-0"
                >
                  <div className="h-10 w-10 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden transition-all">
                    {w.avatar ? (
                      <img src={w.avatar} alt={w.name} className="h-full w-full object-cover" />
                    ) : (
                      getInitials(w.name)
                    )}
                  </div>
                  <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                    {w.name}
                  </span>
                </button>
                {activeId === w.id && activeType === 'personal' && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                )}
                {onAbandon && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onAbandon(w.id, 'personal'); }}
                    title="Abandonar esta agenda"
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 shrink-0"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {filteredEquipos.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="px-4 pt-3 pb-2 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Building2 className="h-3 w-3 shrink-0" />
              Equipos Clínicos
            </div>
            {filteredEquipos.map((e) => (
              <div
                key={e.id}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors group hover:bg-slate-100 dark:hover:bg-slate-800/60',
                  activeId === e.id && activeType === 'equipo'
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : ''
                )}
              >
                <button
                  onClick={() => { onChange(e.id, 'equipo'); setIsOpen(false); }}
                  className="flex items-center gap-3 flex-1 text-left outline-none min-w-0"
                >
                  <div className="h-10 w-10 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden transition-all">
                    {getInitials(e.name)}
                  </div>
                  <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                    {e.name}
                  </span>
                </button>
                {activeId === e.id && activeType === 'equipo' && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                )}
                {onAbandon && (
                  <button
                    onClick={(e2) => { e2.stopPropagation(); onAbandon(e.id, 'equipo'); }}
                    title="Abandonar este equipo"
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 shrink-0"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-slate-200/50 dark:hover:bg-slate-800/50 transition-all border border-transparent hover:border-slate-300 dark:hover:border-slate-700 outline-none focus:ring-2 focus:ring-blue-500 sm:w-65"
        aria-expanded={isOpen}
      >
        <div className="h-10 w-10 rounded-full bg-linear-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0 overflow-hidden">
          {iconContent}
        </div>
        <div className="flex flex-col items-start flex-1 overflow-hidden">
          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {activeType === 'personal' ? 'Agenda Personal' : 'Equipo Clínico'}
          </span>
          <span className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate w-full text-left">
            {activeItem?.name || 'Seleccionar...'}
          </span>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-slate-400 transition-transform duration-200 shrink-0',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Render dropdown via portal to escape overflow:hidden ancestors */}
      {typeof document !== 'undefined' && createPortal(dropdown, document.body)}
    </>
  );
}
